import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";
import { fetchResidence } from "@/lib/api";
import { useFavourites } from "@/contexts/FavouritesProvider";
import { Heart } from "lucide-react";
import { cn } from "@/lib/utils";

const BrionjResidence = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const { has, toggle } = useFavourites();
  useEffect(() => { fetchResidence(id).then(setR); }, [id]);
  if (!r) return <div className="container-rail py-16">Loading…</div>;
  // Gallery shows ONLY the property's curated gallery — the hero is rendered
  // separately above and must never repeat as the first carousel item. We also
  // defensively de-duplicate and strip any accidental match against the hero
  // so the seed data and the carousel never disagree.
  const galleryImages = Array.from(
    new Set((r.gallery || []).filter((g) => g && g !== r.hero_image))
  );
  return (
    <div className="pb-32 md:pb-0" data-testid="brionj-residence">
      <div className="container-rail py-12 md:py-16 grid md:grid-cols-[1.2fr_1fr] gap-12 items-start">
        <div className="p-6 md:p-8 bg-[hsl(var(--card))] border border-border"><img src={r.hero_image} alt={r.name} className="w-full aspect-[4/3] object-cover" /></div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.42em] text-[hsl(var(--brand-accent))]">{r.country}</div>
          <BrandLabel className="mt-2">{r.location}</BrandLabel>
          <BrandHeading level={1} className="mt-3 text-[hsl(var(--brand-accent))]">{r.name}</BrandHeading>
          <div className="mt-3 text-[12px] uppercase tracking-[0.3em] text-foreground/60">{r.bedrooms} bedrooms · {r.bathrooms} bathrooms · sleeps {r.sleeps}</div>
          <p className="mt-6 text-[16px] md:text-[17px] font-body leading-[1.85] editorial-measure text-foreground/85">{r.long_description || r.description}</p>
          {r.indicative_price && <div className="mt-6 text-[14px] font-ui text-[hsl(var(--brand-accent))] uppercase tracking-[0.32em]">{r.indicative_price}</div>}
          <div className="mt-8 flex gap-3 flex-wrap items-center">
            <BrandButton to={`/brionj/enquire/${r.id}`} data-testid="enquire-cta">Enquire</BrandButton>
            <button onClick={() => toggle(r.id, r.name)} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.4em] underline underline-offset-[8px] decoration-[hsl(var(--brand-accent))]" data-testid="brionj-residence-fav"><Heart size={14} className={cn(has(r.id) && "fill-current text-[hsl(var(--brand-accent))]")} /> {has(r.id) ? "Saved" : "Save residence"}</button>
          </div>
        </div>
      </div>

      <div className="container-rail" data-testid="brionj-gallery">
        <BrandLabel>Gallery</BrandLabel>
        <Carousel className="mt-4">
          <CarouselContent>{galleryImages.map((src, i) => <CarouselItem key={`${src}-${i}`} className="basis-2/3 md:basis-1/2 lg:basis-1/3"><div className="p-3 bg-[hsl(var(--card))] border border-border"><img src={src} alt="" className="w-full aspect-[4/3] object-cover" /></div></CarouselItem>)}</CarouselContent>
          <CarouselPrevious /><CarouselNext />
        </Carousel>
      </div>

      <div className="container-rail mt-16 grid md:grid-cols-2 gap-10">
        <div className="p-6 md:p-8 bg-[hsl(var(--card))] border border-border"><BrandLabel>The house</BrandLabel><div className="mt-4 font-display italic text-[28px]">Amenities</div><div className="mt-4 grid grid-cols-2 gap-y-2 text-[14px] font-body">{r.amenities?.map((a) => <div key={a} className="py-2 border-b border-border">{a}</div>)}</div></div>
        <div className="p-6 md:p-8 bg-[hsl(var(--card))] border border-border"><BrandLabel>The staff</BrandLabel><div className="mt-4 font-display italic text-[28px]">Resident hospitality</div><p className="mt-4 text-[15px] font-body leading-[1.85] text-foreground/85 editorial-measure">{r.staff_notes}</p></div>
      </div>

      <Separator className="my-12" />
      <div className="container-rail pb-24 text-center"><BrandHeading level={2} className="max-w-[24ch] mx-auto">When you are ready, we will be.</BrandHeading><div className="mt-6"><BrandButton to={`/brionj/enquire/${r.id}`} data-testid="final-enquire">Begin an enquiry</BrandButton></div></div>
      <div className="fixed bottom-0 inset-x-0 z-30 md:hidden bg-background border-t border-[hsl(var(--brand-accent))]/30 p-3 flex items-center justify-between gap-3" data-testid="brionj-sticky"><div><div className="text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--brand-accent))]">{r.indicative_price || "By enquiry"}</div></div><BrandButton onClick={() => navigate(`/brionj/enquire/${r.id}`)} data-testid="brionj-sticky-enquire">Enquire</BrandButton></div>
    </div>
  );
};
export default BrionjResidence;
